<template>
  <div class="w-full h-full overflow-auto p-3">
    <div
      v-if="!template.fields.length"
      class="flex items-center justify-center h-64 text-base-content/40"
    >
      <div class="text-center">
        <IconTable
          :width="48"
          class="mx-auto mb-3 opacity-30"
        />
        <p class="text-sm">
          No fields yet. Switch to document view to add fields.
        </p>
      </div>
    </div>

    <table
      v-else
      class="table table-sm w-full border border-base-300 rounded-lg"
    >
      <thead class="bg-base-200 sticky top-0 z-10">
        <tr>
          <th class="w-8 text-xs font-medium">#</th>
          <th class="text-xs font-medium">Field Name</th>
          <th class="w-32 text-xs font-medium">Type</th>
          <th class="w-40 text-xs font-medium">Submitter</th>
          <th class="w-20 text-center text-xs font-medium">Required</th>
          <th class="text-xs font-medium">Default Value</th>
          <th class="text-xs font-medium">Label / Title</th>
          <th class="text-xs font-medium">Description</th>
          <th class="w-14 text-center text-xs font-medium">Page</th>
          <th class="w-8" />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(field, index) in sortedFields"
          :key="field.uuid"
          class="hover group"
          :class="{ 'opacity-50': field.type === 'heading' || field.type === 'strikethrough' }"
        >
          <!-- Index -->
          <td class="text-base-content/40 text-xs tabular-nums">
            {{ index + 1 }}
          </td>

          <!-- Field Name -->
          <td class="min-w-[8rem]">
            <input
              v-model="field.name"
              class="input input-xs input-ghost w-full focus:bg-base-200 focus:outline-none min-w-[7rem]"
              :disabled="!!defaultFieldsIndex[field.name]"
              @change="save()"
            >
          </td>

          <!-- Type -->
          <td>
            <FieldType
              v-model="field.type"
              :editable="true"
              :button-width="18"
              menu-classes="mt-1.5 bg-base-100"
              @update:model-value="[maybeUpdateOptions(field), save()]"
            />
          </td>

          <!-- Submitter -->
          <td class="min-w-[8rem]">
            <div class="flex items-center space-x-1.5">
              <span
                class="w-2.5 h-2.5 rounded-full flex-shrink-0"
                :class="submitterColor(field)"
              />
              <select
                v-model="field.submitter_uuid"
                class="select select-xs select-ghost w-full max-w-full focus:outline-none"
                @change="save()"
              >
                <option
                  v-for="sub in template.submitters"
                  :key="sub.uuid"
                  :value="sub.uuid"
                >
                  {{ sub.name }}
                </option>
              </select>
            </div>
          </td>

          <!-- Required -->
          <td class="text-center">
            <input
              v-if="field.type !== 'phone'"
              v-model="field.required"
              type="checkbox"
              class="checkbox checkbox-xs no-animation rounded"
              @change="save()"
            >
          </td>

          <!-- Default Value -->
          <td class="min-w-[7rem]">
            <input
              v-if="hasDefaultValue(field)"
              v-model="field.default_value"
              :type="field.type === 'number' ? 'number' : 'text'"
              class="input input-xs input-ghost w-full focus:bg-base-200 focus:outline-none min-w-[6rem]"
              @change="save()"
            >
            <span
              v-else
              class="text-base-content/25 text-xs"
            >—</span>
          </td>

          <!-- Label/Title -->
          <td class="min-w-[7rem]">
            <input
              v-model="field.title"
              class="input input-xs input-ghost w-full focus:bg-base-200 focus:outline-none min-w-[6rem]"
              placeholder="Same as name"
              @change="save()"
            >
          </td>

          <!-- Description -->
          <td class="min-w-[8rem]">
            <input
              v-model="field.description"
              class="input input-xs input-ghost w-full focus:bg-base-200 focus:outline-none min-w-[7rem]"
              @change="save()"
            >
          </td>

          <!-- Page -->
          <td class="text-center">
            <span
              v-if="firstArea(field)"
              class="text-xs text-base-content/60"
            >{{ firstArea(field).page + 1 }}</span>
            <span
              v-else
              class="text-base-content/25 text-xs"
            >—</span>
          </td>

          <!-- Remove -->
          <td>
            <button
              class="btn btn-xs btn-ghost text-error opacity-0 group-hover:opacity-100 transition-opacity"
              :title="t('remove')"
              @click.prevent="removeField(field)"
            >
              <IconX :width="14" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <div
      v-if="template.fields.length"
      class="mt-2 text-xs text-base-content/40 pl-1"
    >
      {{ template.fields.length }} field{{ template.fields.length !== 1 ? 's' : '' }} total
    </div>
  </div>
</template>

<script>
import FieldType from './field_type'
import { IconX, IconTable } from '@tabler/icons-vue'

const SUBMITTER_COLORS = ['bg-sky-500', 'bg-purple-500', 'bg-emerald-500', 'bg-orange-500', 'bg-pink-500']

const NO_DEFAULT_VALUE_TYPES = ['signature', 'initials', 'image', 'file', 'stamp', 'payment', 'verification', 'kba', 'checkbox', 'multiple', 'radio', 'select', 'heading', 'strikethrough']

export default {
  name: 'FieldsDatagrid',
  components: { FieldType, IconX, IconTable },
  inject: ['template', 'save', 't', 'removeField'],
  props: {
    defaultFields: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    defaultFieldsIndex () {
      return this.defaultFields.reduce((acc, f) => {
        acc[f.name] = f
        return acc
      }, {})
    },
    sortedFields () {
      const submitterOrder = {}
      this.template.submitters.forEach((s, i) => {
        submitterOrder[s.uuid] = i
      })
      return [...this.template.fields].sort((a, b) => {
        const subA = submitterOrder[a.submitter_uuid] ?? 999
        const subB = submitterOrder[b.submitter_uuid] ?? 999
        if (subA !== subB) return subA - subB
        return this.template.fields.indexOf(a) - this.template.fields.indexOf(b)
      })
    }
  },
  methods: {
    submitterColor (field) {
      const idx = this.template.submitters.findIndex((s) => s.uuid === field.submitter_uuid)
      return SUBMITTER_COLORS[Math.max(idx, 0) % SUBMITTER_COLORS.length]
    },
    firstArea (field) {
      return field.areas?.[0]
    },
    hasDefaultValue (field) {
      return !NO_DEFAULT_VALUE_TYPES.includes(field.type)
    },
    maybeUpdateOptions (field) {
      if (['select', 'radio', 'multiple'].includes(field.type) && !field.options?.length) {
        field.options = [{ value: '', uuid: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) }]
      }
    }
  }
}
</script>
